import css from 'styled-jsx/css';
import { display, sansSerif, serif } from 'styles/css-constants';
import { sharedPalette, warmGrays } from 'styles/colors/constants';
import { MQ_LARGE, MQ_MEDIUM, MQ_SMALL_ONLY } from 'constants/mq';

export default css`
  .SiteRecipe__interactions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    border-top: 2px solid ${sharedPalette.kYellow};
    padding-top: 0.25rem;
    padding-left: 0.5rem;
    font-size: 12px;
  }

  .SiteRecipe__jumpToComments,
  .SiteRecipe :global(.PrintButton__button) {
    padding: 0.375rem 0.5rem 0.25rem;
    line-height: 1.2;
  }

  .SiteRecipe__jumpToComments {
    display: none;
    align-items: center;
    border-right: 1px solid #fff;
    color: #000;
    font-family: ${sansSerif};
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .SiteRecipe__jumpToComments :global(svg) {
    margin-right: 0.25rem;
  }

  .SiteRecipe :global(.PrintButton) {
    display: flex;
    align-items: center;
    border-right: 1px solid #fff;
  }

  .SiteRecipe :global(.RecipeRating) {
    position: relative;
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-start;
    padding-right: 0.5rem;
    padding-left: 0.5rem;
  }

  .SiteRecipe :global(.RecipeRating__anchor) {
    position: absolute;
    top: 1rem;
    left: 0;
    width: 100%;
  }

  .SiteRecipe :global(.RecipeRating__anchor .Tooltip) {
    right: 0.25rem;
  }

  /* Show comments button as soon as we can */
  @media screen and (min-width: 400px) {
    .SiteRecipe__jumpToComments {
      display: flex;
    }
  }

  @media screen and ${MQ_LARGE} {
    .SiteRecipe__jumpToComments,
    .SiteRecipe :global(.PrintButton__button) {
      padding: 0 1rem 0 0;
      line-height: 1.2;
    }

    .SiteRecipe :global(.RecipeRating) {
      height: 24px;
    }
  }

  /* Chicory overrides because we can */
  @media screen and ${MQ_SMALL_ONLY} {
    :global(.chicory-noprint) {
      padding-left: 1rem;
    }
  }

  @media screen and ${MQ_MEDIUM} {
    :global(.chicory-noprint) {
      grid-column: span 2;
    }
  }
`;

export const recipeStyles = css`
  .Recipe {
    padding: 1rem 0;
  }

  .Recipe :global(a) {
    border-bottom: 1px solid currentColor;
    color: #000;
  }

  .Recipe h3,
  .Recipe :global(.Recipe__suppliesGroupName) {
    font-family: ${sansSerif};
    font-size: var(--baseFontSize);
    font-weight: bold;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .Recipe :global(.Recipe__ingredient),
  .Recipe :global(.Recipe__supplyItem) {
    position: relative;
    margin-bottom: 0.75rem;
    line-height: 1.35;
  }

  /* 
 * Because of a Recipe editor limitation we have to force
 * text to be inline 
 */
  .Recipe :global(.Recipe__ingredient p),
  .Recipe :global(.Recipe__ingredient span),
  .Recipe :global(.Recipe__instructionsGroup p),
  .Recipe :global(.Recipe__instructionsGroup span) {
    display: inline;
  }

  .Recipe :global(.Recipe__ingredients),
  .Recipe :global(.Recipe__supplies) {
    padding-left: 0;
    list-style: none;
    font-family: ${sansSerif};
    font-size: var(--baseFontSize);
  }

  .Recipe__ingredientsAddOn,
  .Recipe__ingredientsSection,
  .Recipe__suppliesSection {
    background-color: #f5f3f3;
  }

  .Recipe__ingredientsAddOn {
    border-bottom: 1px solid ${warmGrays[3]};
  }

  .Recipe :global(.Recipe__ingredientsGroup) {
    padding-bottom: 1rem;
  }

  .Recipe :global(.Recipe__ingredientsGroupName),
  .Recipe :global(.Recipe__instructionsGroup h4) {
    margin-bottom: 0.75rem;
    font-family: ${serif};
    font-size: var(--baseFontSize);
    font-style: italic;
    font-weight: normal;
  }

  .Recipe__ingredientsHeading,
  .Recipe__instructionsHeading,
  .Recipe :global(.Recipe__suppliesGroupName) {
    margin-bottom: 0.75rem;
  }

  .Recipe__ingredientsHeading {
    padding-top: 1.25rem;
  }

  .Recipe :global(.Recipe__instructions) {
    list-style: none;
    counter-reset: listCounter;
  }

  .Recipe :global(.Recipe__instructionsGroup) {
    margin-bottom: 1.25rem;
  }

  .Recipe__instructionsHeading {
    padding-top: 1.25rem;
  }

  .Recipe :global(.Recipe__instructionStep),
  .Recipe__notes {
    font-size: var(--fontScale1);
    line-height: 1.5;
  }

  .Recipe :global(.Recipe__instructionStep) {
    position: relative;
    margin-top: 0.5rem;
    border-top: 1px solid ${warmGrays[1]};
    padding-top: 0.5rem;
  }

  .Recipe :global(.Recipe__instructionStep a),
  .Recipe .Recipe__notes a {
    border-bottom: 1px solid currentColor;
    color: #000;
  }

  .Recipe :global(.Recipe__instructionStep p),
  .Recipe__notes :global(p) {
    margin-bottom: 1em;
  }

  .Recipe :global(.Recipe__instructionStep p) {
    margin-bottom: 0;
  }

  .Recipe :global(.Recipe__instructionStep .Figure) {
    padding-top: 1rem;
    padding-bottom: 0.5rem;
  }

  .Recipe :global(.Recipe__instructionStep:first-of-type) {
    margin-top: 0;
    border-top: none;
    padding-top: 0;
  }

  .Recipe :global(.Recipe__instructionStepContent) {
    margin-bottom: 0.5rem;
  }

  .Recipe :global(.Recipe__instructionStepContent::before) {
    margin-right: 0.5rem;
    counter-increment: listCounter;
    content: counter(listCounter);
    display: inline-block;
    color: ${sharedPalette.watermelon};
    font-family: ${sansSerif};
    font-weight: bold;
  }

  .Recipe__notesHeading {
    margin-bottom: 1rem;
  }

  .Recipe__notesSection {
    border-top: 1px solid ${warmGrays[3]};
    border-bottom: 1px solid ${warmGrays[3]};
    padding-top: 1.25rem;
    padding-bottom: 0.25rem;
  }

  .Recipe__supplementalInfo {
    margin-bottom: 1.5rem;
    display: flex;
  }

  .Recipe :global(.Recipe__suppliesGroup) {
    padding-bottom: 0.75rem;
  }

  .Recipe__suppliesSection {
    border-bottom: 1px solid ${warmGrays[3]};
    padding-top: 1.25rem;
  }

  .Recipe :global(.Recipe__supplyItem a p) {
    border-bottom: 1px solid currentColor;
  }

  .Recipe :global(.Recipe__supplyItem span),
  .Recipe :global(.Recipe__supplyItem p) {
    display: inline;
  }

  .Recipe__time {
    flex: 0 0 66.66%;
    display: flex;
  }

  .Recipe :global(.Recipe__timeEntry) {
    flex: 0 0 50%;
    font-family: ${sansSerif};
    font-size: var(--fontScaleNeg2);
    line-height: 1.35;
  }

  .Recipe :global(.Recipe__timeEntry:first-child) {
    padding-right: 0.25rem;
    padding-left: 0.25rem;
  }

  .Recipe :global(.Recipe__timeEntry:nth-child(2)) {
    padding-left: 0.5rem;
  }

  .Recipe :global(.Recipe__timeLabel) {
    margin-bottom: 0.125rem;
    display: block;
    font-weight: bold;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .Recipe__title {
    margin-bottom: 1.5rem;
    font-family: ${display};
    font-size: var(--fontScale4);
    font-weight: normal;
    line-height: 1.3;
  }

  .Recipe__yield {
    flex: 0 0 33.33%;
    padding-right: 0.5rem;
    font-family: ${sansSerif};
    font-size: var(--fontScaleNeg2);
    line-height: 1.35;
  }

  .Recipe__yieldLabel {
    margin-bottom: 0.125rem;
    display: block;
    font-weight: bold;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .Recipe :global(.RecipeNutritionalGuide) {
    border-top: 1px solid ${warmGrays[3]};
    border-bottom: 1px solid ${warmGrays[3]};
    padding: 0.25rem 0;
  }

  .Recipe :global(.RecipeNutritionalGuide__nutrition) {
    padding: 1rem 1rem 0.5rem;
  }

  .Recipe :global(.RecipeNutritionalGuide__nutrition a) {
    border-bottom: none;
  }

  .Recipe :global(.RecipeNutritionalGuide__toggle) {
    display: flex;
    align-items: center;
  }

  .Recipe :global(.RecipeNutritionalGuide__toggle button) {
    margin: 0 0.5rem;
    display: block;
    width: 100%;
    padding: 0.25rem;
    letter-spacing: 0.02em;
    text-align: center;
    text-transform: uppercase;
  }

  .Recipe :global(.RecipeNutritionalGuide__toggle button svg) {
    font-size: 0.8em;
  }

  @media ${MQ_SMALL_ONLY} {
    .Recipe h3,
    .Recipe__ingredientsAddOn,
    .Recipe :global(.Recipe__ingredientsGroup),
    .Recipe :global(.Recipe__instructionsGroup),
    .Recipe__notes :global(p),
    .Recipe__supplementalInfo,
    .Recipe :global(.Recipe__supplies),
    .Recipe :global(.Recipe__suppliesGroupName),
    .Recipe__title {
      padding-right: 1rem;
      padding-left: 1rem;
    }
  }

  @media ${MQ_MEDIUM} {
    .Recipe__ingredientsSection,
    .Recipe__suppliesSection {
      padding-right: 1rem;
      padding-left: 1rem;
    }
  }

  @media ${MQ_LARGE} {
    .Recipe h3,
    .Recipe :global(.Recipe__ingredientsGroupName),
    .Recipe :global(.Recipe__instructionsGroup h4),
    .Recipe :global(.Recipe__suppliesGroupName) {
      margin-bottom: 0.75rem;
    }

    .Recipe :global(.Recipe__instructionStep) {
      margin-top: 0.75rem;
      border-top: 1px solid ${warmGrays[1]};
      padding-top: 0.75rem;
      /* Interim size for recipe text alone */
      font-size: calc(var(--baseFontSize) * 1.125);
    }

    .Recipe :global(.Recipe__instructionStepContent),
    .Recipe__notes :global(p) {
      line-height: 1.55;
    }

    .Recipe :global(.Recipe__instructionStepContent) {
      margin-bottom: 0.75rem;
    }

    .Recipe :global(.Recipe__notes) {
      /* Interim size for recipe text alone */
      font-size: calc(var(--baseFontSize) * 1.125);
    }

    .Recipe__supplementalInfo,
    .Recipe :global(.Recipe__time) {
      grid-column-gap: 0.75rem;
    }

    .Recipe :global(.Recipe__timeEntry),
    .Recipe__yield {
      font-size: var(--fontScaleNeg1);
    }

    .Recipe__title {
      font-size: var(--fontScale3);
    }
  }

  @supports (display: grid) {
    .Recipe__supplementalInfo {
      display: grid;
      grid-template-columns: calc((100% - 2rem) / 3) 1fr;
    }

    .Recipe__time {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    .Recipe :global(.Recipe__timeEntry:first-child) {
      margin-left: 0.5rem;
      border-left: 1px solid ${warmGrays[3]};
      padding-right: 0;
      padding-left: 0.5rem;
    }

    .Recipe :global(.Recipe__timeEntry:nth-child(2)) {
      margin-left: 0.5rem;
      border-left: 1px solid ${warmGrays[3]};
      padding-left: 0.5rem;
    }

    .Recipe__yield {
      padding: 0;
    }

    @media screen and ${MQ_MEDIUM} {
      .Recipe :global(.Recipe__ingredientsGroups) {
        display: grid;
        grid-template-columns: 1fr fit-content(50%);
        grid-column-gap: 1.5rem;
      }
    }
  }
`;
